import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, deleteDoc, doc, query, orderBy, updateDoc } from 'firebase/firestore';

// Componentes y Formularios de UI
import Modal from '../../ui/Modal';
import DesignForm from '../../ui/DesignForm';

// Iconos
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;

export default function DisenosCatalogo({ db, showModal }) {
    const [designs, setDesigns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentDesign, setCurrentDesign] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        const q = query(collection(db, 'inventory_designs'), orderBy('name'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setDesigns(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error al cargar diseños: ", error);
            showModal("Error", "No se pudieron cargar los diseños.");
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, showModal]);

    const handleSaveDesign = async (formData) => {
        const dataToSave = { ...formData, sizes: formData.sizes.split(',').map(s => s.trim().toUpperCase()).filter(Boolean), };
        try {
            if (currentDesign) {
                await updateDoc(doc(db, 'inventory_designs', currentDesign.id), dataToSave);
                showModal("Éxito", `Diseño "${dataToSave.name}" actualizado.`);
            } else {
                await addDoc(collection(db, 'inventory_designs'), dataToSave);
                showModal("Éxito", `Diseño "${dataToSave.name}" creado.`);
            }
            setIsModalOpen(false);
            setCurrentDesign(null);
        } catch (error) {
            console.error("Error al guardar diseño:", error);
            showModal("Error", `No se pudo guardar el diseño: ${error.message}`);
        }
    };

    const handleDeleteDesign = (design) => {
        showModal(
            `Confirmar eliminación`,
            `¿Seguro que quieres eliminar el diseño "${design.name}"?`,
            [
                <button key="confirm" onClick={async () => {
                    try {
                        await deleteDoc(doc(db, 'inventory_designs', design.id));
                        showModal("Éxito", `Diseño "${design.name}" eliminado.`);
                    } catch (error) {
                        showModal("Error", "No se pudo eliminar el diseño.");
                    }
                }} className="btn-primary bg-red-600 hover:bg-red-700">Eliminar</button>,
                <button key="cancel" onClick={() => showModal('', '', null)} className="btn-secondary">Cancelar</button>
            ]
        );
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-800">Catálogo de Diseños</h3>
                <button onClick={() => { setCurrentDesign(null); setIsModalOpen(true); }} className="btn-primary"> + Crear Diseño </button>
            </div>
            <p className="text-gray-600 mb-6"> Gestiona los diseños que se estampan en las playeras, incluyendo las tallas disponibles para cada uno. </p>
            {isLoading ? <p>Cargando diseños...</p> : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Nombre del Diseño</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Tallas Disponibles</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {designs.map(design => (
                                <tr key={design.id} className="border-b hover:bg-sky-50">
                                    <td className="p-3 font-medium">{design.name}</td>
                                    <td className="p-3">{design.sizes?.join(', ')}</td>
                                    <td className="p-3 text-center">
                                        <div className="flex justify-center items-center space-x-2">
                                            <button onClick={() => { setCurrentDesign(design); setIsModalOpen(true); }} className="text-sky-600 hover:text-sky-800 p-1" title="Editar"><EditIcon /></button>
                                            <button onClick={() => handleDeleteDesign(design)} className="text-red-500 hover:text-red-700 p-1" title="Eliminar"><TrashIcon /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {designs.length === 0 && <p className="text-center text-gray-500 py-8">No hay diseños creados.</p>}
                </div>
            )}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={currentDesign ? "Editar Diseño" : "Nuevo Diseño"}>
                <DesignForm onSave={handleSaveDesign} initialData={currentDesign} isLoading={isLoading} onCancel={() => setIsModalOpen(false)} />
            </Modal>
        </div>
    );
};